// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/execution-plans/ExecutionSummaryCard.jsx

import React from "react";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => displayValue(item, "")).filter(Boolean).join(", ") || fallback;
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try { return JSON.stringify(value); } catch (error) { return fallback; }
  }
  return String(value);
}

function humanize(value, fallback = "—") {
  const raw = displayValue(value, "").trim();
  if (!raw) return fallback;
  const exact = {
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    okta: "Okta",
    cloudflare: "Cloudflare",
    github: "GitHub",
    "google workspace": "Google Workspace",
  };
  const normalized = raw.replace(/_/g, " ").replace(/-/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();
  if (exact[lower]) return exact[lower];
  return normalized.split(" ").map((word) => {
    const key = word.toLowerCase();
    if (exact[key]) return exact[key];
    if (/^[A-Z0-9_]{2,}$/.test(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(" ");
}

const sectionStyle = {
  border: "1px solid rgba(96, 165, 250, 0.18)",
  borderRadius: 18,
  background: "linear-gradient(180deg, rgba(15, 23, 42, 0.82), rgba(15, 23, 42, 0.58))",
  boxShadow: "0 18px 48px rgba(2, 6, 23, 0.22)",
  padding: 18,
};

const sectionTitleStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 12,
};

const eyebrowStyle = {
  display: "block",
  color: "#93c5fd",
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 4,
};

const titleStyle = {
  color: "#f8fafc",
  fontSize: 15,
  fontWeight: 900,
  margin: 0,
};

const bodyTextStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 999,
  border: "1px solid rgba(148, 163, 184, 0.25)",
  background: "rgba(15, 23, 42, 0.74)",
  color: "#dbeafe",
  fontSize: 11,
  fontWeight: 850,
  padding: "6px 10px",
  whiteSpace: "nowrap",
};

export function ExecutionPlanSection({ eyebrow, title, badge, children }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionTitleStyle}>
        <div>
          {eyebrow ? <span style={eyebrowStyle}>{eyebrow}</span> : null}
          <h3 style={titleStyle}>{title}</h3>
        </div>
        {badge ? <span style={pillStyle}>{badge}</span> : null}
      </div>
      {children}
    </section>
  );
}

export function EmptyPanel({ title = "No execution planning context", message = "Execution planning data will appear here once the task plan is available." }) {
  return (
    <div style={{ ...sectionStyle, borderStyle: "dashed", textAlign: "left" }}>
      <strong style={{ color: "#f8fafc", display: "block", marginBottom: 6 }}>{title}</strong>
      <p style={bodyTextStyle}>{message}</p>
    </div>
  );
}

export function MetricTile({ label, value, footnote }) {
  return (
    <div style={{
      border: "1px solid rgba(148, 163, 184, 0.18)",
      borderRadius: 16,
      background: "rgba(15, 23, 42, 0.62)",
      padding: 14,
      minHeight: 92,
    }}>
      <span style={{ color: "#93c5fd", fontSize: 11, fontWeight: 850, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <strong style={{ color: "#f8fafc", display: "block", fontSize: 20, marginTop: 6 }}>{displayValue(value)}</strong>
      {footnote ? <small style={{ color: "#94a3b8", display: "block", marginTop: 5 }}>{footnote}</small> : null}
    </div>
  );
}

export { clean, displayValue, humanize, bodyTextStyle, pillStyle };


export default function ExecutionSummaryCard({ plan }) {
  const task = plan?.task || {};
  const impact = plan?.business_impact || {};
  const changeWindow = plan?.change_window || {};
  const guardrails = plan?.guardrails || {};

  return (
    <ExecutionPlanSection eyebrow="Execution Plan" title={plan?.title || "Execution plan"} badge={plan?.status || "generated"}>
      <p style={bodyTextStyle}>{plan?.summary || "Operator-reviewed execution plan for the selected task."}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 16 }}>
        <MetricTile label="Estimated Time" value={plan?.estimated_time || impact.estimated_time} />
        <MetricTile label="Business Impact" value={impact.business_impact || "Review"} />
        <MetricTile label="Risk Reduction" value={impact.risk_reduction || "Review"} />
        <MetricTile label="Validation Confidence" value={impact.validation_confidence || "Review"} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 14 }}>
        <div style={{ border: "1px solid rgba(148, 163, 184, 0.16)", borderRadius: 14, padding: 12, background: "rgba(2, 6, 23, 0.26)" }}>
          <span style={eyebrowStyle}>Task</span>
          <p style={bodyTextStyle}>{humanize(task.rule_id || "Finding")} · {displayValue(task.resource_id, "resource pending")}</p>
        </div>
        <div style={{ border: "1px solid rgba(148, 163, 184, 0.16)", borderRadius: 14, padding: 12, background: "rgba(2, 6, 23, 0.26)" }}>
          <span style={eyebrowStyle}>Recommended Window</span>
          <p style={bodyTextStyle}>{displayValue(changeWindow.recommendation)} · {displayValue(changeWindow.window)}</p>
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
        <span style={pillStyle}>No autonomous execution</span>
        <span style={pillStyle}>Operator approval required</span>
        <span style={pillStyle}>No state mutation</span>
        {guardrails.maintenance_window_scheduling === false ? <span style={pillStyle}>Window not scheduled</span> : null}
      </div>
    </ExecutionPlanSection>
  );
}
